import { css } from '@volkswagen-onehub/components-core';

export const camelToKebab = (str: string) =>
  str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

export const applyCustomStyles = ($customStyles?: React.CSSProperties) => {
  if (!$customStyles || typeof $customStyles !== 'object') return '';

  const rules = Object.entries($customStyles)
    .filter(
      ([, value]) => typeof value === 'string' || typeof value === 'number'
    )
    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
    .join('\n');

  return css`
    ${rules}
  `;
};

export const applyCustomStylesToChildren = (
  $customStyles?: React.CSSProperties
) => {
  if (!$customStyles || typeof $customStyles !== 'object') return '';

  const rules = applyCustomStyles($customStyles);

  return css`
    & > * {
      ${rules}
    }
  `;
};

/**
 * Separates nested selectors (e.g., '> button', '> a', '& > span') from regular CSS properties
 * @param customStyles - The custom styles object that may contain nested selectors
 * @returns An object with separated nestedSelectors and regularStyles
 */
export const separateNestedSelectors = (
  customStyles?: React.CSSProperties | Record<string, any>
): {
  nestedSelectors: Array<[string, any]>;
  regularStyles: Record<string, any>;
} => {
  if (!customStyles || typeof customStyles !== 'object') {
    return { nestedSelectors: [], regularStyles: {} };
  }

  // Separate nested selectors (e.g., '> button', '> a', '> span') from regular CSS properties
  const nestedSelectors = Object.entries(customStyles).filter(
    ([key]) => key.startsWith('>') || key.startsWith('&')
  );

  // Regular CSS properties (camelCase keys that aren't selectors)
  const regularStyles = Object.fromEntries(
    Object.entries(customStyles).filter(
      ([key]) => !key.startsWith('>') && !key.startsWith('&')
    )
  );

  return { nestedSelectors, regularStyles };
};

/**
 * Applies nested selector styles as CSS
 * @param nestedSelectors - Array of [selector, styles] tuples
 * @returns CSS template string with nested selector rules
 */
export const applyNestedSelectors = (nestedSelectors: Array<[string, any]>) => {
  return nestedSelectors.map(([selector, styles]) => {
    if (typeof styles === 'object' && styles !== null) {
      // Convert camelCase CSS properties to kebab-case
      const cssProps = Object.entries(styles)
        .map(([key, value]) => {
          const kebabKey = camelToKebab(key);
          return `${kebabKey}: ${value};`;
        })
        .join('\n');
      return css`
        ${selector} {
          ${cssProps}
        }
      `;
    }
    return '';
  });
};
